import Token from "../../../../config/token.service";
import adService from "../../../../services/ad.service";
import mediaService from "../../../../services/media.service";
import { AlertColor } from "@mui/material";
import { Campaign } from "../../../../types/campaign";
import { delay } from "./delay";

const uploadMediaMessage = "Uploading media.";
const verifyingMediaMessage = "Verifying uploaded media. This may take a while."

const mediaUploadErrorMessage = "Could not upload the selected media.";
const adCreateErrorMessage = "Could not create the ad.";
const adUpdateErrorMessage = "Could not update the ad.";

export async function handleAdCreate(data: any, setAlert: (v: string) => void, setSeverity: (v: AlertColor) => void) {
    let mediaFileNames = [];
    if (data.media) {
        setSeverity("info");
        setAlert(uploadMediaMessage);
        mediaFileNames = await handleMediaUpload(data.media, setAlert, setSeverity);
        if (!mediaFileNames) {
            setSeverity("error");
            setAlert(mediaUploadErrorMessage);
            return -1;
        }
    }
    const response = await adService.create(
        data.ageMin,
        data.ageMax,
        data.hittingHand,
        data.throwingHand,
        data.city,
        data.state,
        data.country,
        data.positions,
        data.dailyBudget,
        data.startTime,
        data.endTime,
        data.objective,
        data.nickname,
        data.title,
        data.headline,
        data.link,
        mediaFileNames.length > 0 ? mediaFileNames : null,
        data.templateId,
    );
    if (response === -1) {
        setAlert(adCreateErrorMessage);
    }

    return response;
}

export async function handleAdUpdate(
    data: any,
    setAlert: (v: string) => void,
    setSeverity: (v: AlertColor) => void,
    mediaChanged: boolean,
    ad: Campaign
) {
    let mediaFileNames = ad.media.media;
    if (mediaChanged) {
        setSeverity("info");
        setAlert(uploadMediaMessage);
        mediaFileNames = await handleMediaUpload(data.media, setAlert, setSeverity);
        if (!mediaFileNames) {
            setSeverity("error");
            setAlert(mediaUploadErrorMessage);
            return -1;
        }
    }
    const response = await adService.update(
        ad.id,
        Token.getUser().id,
        data.ageMin,
        data.ageMax,
        data.hittingHand,
        data.throwingHand,
        data.city,
        data.state,
        data.country,
        data.positions,
        data.dailyBudget,
        data.startTime,
        data.endTime,
        data.objective,
        data.nickname,
        data.title,
        data.headline,
        data.link,
        mediaFileNames.length > 0 ? mediaFileNames : null,
        data.templateId,
    );
    if (response === -1) {
        setAlert(adUpdateErrorMessage);
    }

    return response;
}

async function handleMediaUpload(media: File[], setAlert: (v: string) => void, setSeverity: (v: AlertColor) => void) {
    const uid = Token.getUser().id;
    const extensions = media.map((file) => file.type.split("/")[1]);
    const generateResponse = await mediaService.generate(uid, extensions);

    if (generateResponse.c !== 1) {
        return false;
    }

    const mediaFileNames = generateResponse.v.mediaFileNames;
    const mediaPresignedUrls = generateResponse.v.mediaPresignedUrls;
    const uploadResponse = mediaService.upload(mediaPresignedUrls, media);

    if (!uploadResponse) {
        return false;
    }

    setSeverity("info");
    setAlert(verifyingMediaMessage);
    const verifyResponse = await handleVerifyMedia(uid, mediaFileNames);

    if (!verifyResponse) {
        return false;
    }

    return mediaFileNames;
}

async function handleVerifyMedia(id: number, mediaFileNames: string[]) {
    let count = 0;
    const fileCount = mediaFileNames.length;

    while (count < 20) {
        const response = await mediaService.verify(id, mediaFileNames);
        if (response.c === 1) {
            const results = response.v.results;
            const status = response.v.status;

            if (results.length === fileCount && status.length === fileCount) {
                const allResultsTrue = results.every((res: boolean) => res);
                const allStatusTrue = status.every((stat: string) => stat === "ok");
                if (allResultsTrue && allStatusTrue) {
                    return true;
                }
            }
        }

        count += 1;

        if (response.c === 0 && count > 10) {
            break;
        }
        
        await delay(3000); // Wait 3 seconds
    }
    
    return false;
}
